import type { GetStaticProps } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { UploadPageLayout } from "components/modules/Layout/UploadPageLayout/UploadPageLayout";
import { BulkResizerContent } from "components/modules/Content/BulkResizerContent/BulkResizerContent";
import { BulkResizerTutorial } from "components/modules/Tutorial/BulkResizerTutorial/BulkResizerTutorial";
import { Navbar } from "components/modules/Navigation/Navbar/Navbar";
import Head from "next/head";
import { commonLanguageFiles } from "client/config";
import { LanguageProvider } from "client/contexts/LanguageProvider";
import { translateFunc } from "client/utils/translateFunc";
import { Translate } from "components/common/Translate/Translate";
import dynamic from "next/dynamic";

const BulkResizeUploader = dynamic<{}>(() =>
    import("components/modules/Uploader/BulkResize/BulkResizeUploader").then((mod) => mod.BulkResizeUploader),
);

const Page = () => {
    const title = translateFunc("page-title");
    const description = translateFunc("meta-description");

    return (
        <>
            <Head>
                <title>{title}</title>
                <meta name="description" content={description} />
                <meta property="og:type" content="website" />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
                <meta property="og:image" content="/social.png" />
                <meta property="twitter:card" content="summary" />
                <meta property="twitter:title" content={title} />
                <meta property="twitter:description" content={description} />
            </Head>
            <UploadPageLayout
                navbar={<Navbar url="/bulk-resize" />}
                title={<Translate keyName="title" />}
                subTitle={<Translate keyName="sub-title" />}
                uploader={<BulkResizeUploader />}
                content={<BulkResizerContent />}
                tutorial={<BulkResizerTutorial />}
                pageUrl={"/bulk-resize"}
            />
        </>
    );
};

const files = ["bulk-resize-landing", "uploader", ...commonLanguageFiles];

const LanguageWrapper = () => (
    <LanguageProvider files={files}>
        <Page />
    </LanguageProvider>
);

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
    props: {
        ...(await serverSideTranslations(locale ?? "", files)),
    },
});

export default LanguageWrapper;
